/**
 * Whether the competing-listings (active / pending) search RAN, said in words and in the claim the
 * report is built from. Unknown is never zero: "0 on the market now" is printed only when a search
 * ran and found none. Display-only; nothing here touches a figure.
 *
 * The MLS block is the server's (`mls_rung.fetch_comps` → `source_cascade` → `result.mls`). The
 * competing search has its own radius, so the radius shown is `actives_radius_mi`, never the sold
 * search's `radius_mi`.
 */
import type { CascadeMlsResult } from "./cascade";

export interface ActivesSearchClaim {
  searched: boolean;
  radius_mi?: number | null;
  found?: number | null;
  reason?: string;
}

const NO_SOURCE =
  "No source of active listings was available for this brief: MLS is not turned on for this account, and no file with listings was read.";

function miles(n: number): string {
  return String(Number(n.toFixed(2)));
}

/** What to send in `meta.actives_search`, or null when the listings themselves are the answer. */
export function activesSearchClaim(a: {
  mls: CascadeMlsResult | undefined;
  listingCount: number;
  /** How many competing-listing rows the agent's FILE produced. A file that was merely read (a tax
   * notice, a CAD sheet) is not a search for competing homes; only listing rows are an answer. */
  fileListingRows: number;
}): ActivesSearchClaim | null {
  // The rows on screen speak for themselves, from the file or from the MLS.
  if (a.listingCount > 0 || a.fileListingRows > 0) return null;
  const m = a.mls;
  if (m?.actives_searched === true) {
    return {
      searched: true,
      radius_mi: typeof m.actives_radius_mi === "number" ? m.actives_radius_mi : null,
      found: typeof m.actives_found === "number" ? m.actives_found : null,
    };
  }
  if (m && m.actives_searched === false) {
    return { searched: false, reason: m.actives_reason || "The competing-homes search did not run." };
  }
  if (m) {
    // An older server: the MLS ran but never said whether it searched actives. Not a zero.
    return { searched: false, reason: "The server did not report a search for competing homes." };
  }
  return { searched: false, reason: NO_SOURCE };
}

/** The one line the comps step shows about competing homes, or null before any search. */
export function activesSearchLine(
  cascadePresent: boolean, mls: CascadeMlsResult | undefined, fileListingRows = 0,
): string | null {
  if (!cascadePresent) return null;
  // The agent's own file supplied competing listings and they are shown right below: not "not searched".
  if (!mls && fileListingRows > 0) return null;
  if (!mls) return "Competing homes were not searched: MLS is not turned on for this account.";
  if (mls.actives_searched === true && typeof mls.actives_radius_mi === "number") {
    const r = miles(mls.actives_radius_mi);
    return mls.actives_found
      ? `Active and pending listings: ${mls.actives_found} found within ${r} mi.`
      : `No active or pending listings within ${r} mi.`;
  }
  return mls.actives_reason || "Competing homes were not searched.";
}
